import VidaSection from "components/@vida/vida-section";
import React, {FC} from "react";
import Divider from "../divider";
import Spacer from "../spacer";
import Note from "../note";

const NetworkFaultTracingOverview: FC = () => {
  return <div>
    <VidaSection title="" id="network-fault-tracing-overview">
      <Divider/>
      <h1>Network Fault Tracing Overview</h1>

      {/* Network fault tracing overview VDS */}
      <Spacer/>
      <VidaSection title="Network fault tracing overview VDS" id="network-fault-tracing-overview-vds">
        <p><strong>Fault tracing a vehicle{"'"}s network</strong></p>
        <p>This procedure describes how to fault trace a VDS protocol-based vehicle{"'"}s network. The description
          includes information about how the information for a network in the vehicle is presented in the ranked list
          and how to work with the ranked list to find a solution.
        </p>
        <Note body={<p>This procedure is only applicable for VDS protocol-based vehicles.</p>}/>
        <p>The procedure is based on a case where a customer has been complaining that her vehicle, an XC90 2016 is
          behaving strangely. Some features in the vehicle do not always work properly. You are about to start fault
          tracing the vehicle that is in the workshop and connected to the VIDA workstation.
        </p>
        <p><strong>Connect the vehicle</strong></p>
        <ol>
          <li>Start by connecting the vehicle via cable or
            wirelessly.<br/>{"->"} The <strong>Planning &amp; Diagnostics</strong> tab is opened by default as soon as
            the vehicle is connected and assigned its own tab. A network fault is indicated by the <img className="icon"
                                                                                                        src="https://cdn.volvotechinfo.com/vida-basic-training/Network%20Fault%20Tracing%20Overview/Network%20fault%20tracing%20overview%20VDS_image1.jpg"
                                                                                                        alt="Network fault tracing overview VDS_image1"/> icon
            next to the Network tab in the left-hand menu panel. A network fault can generate a lot of unclear faults
            and should therefore always be prioritized and remedied first.
          </li>
        </ol>
        <p><strong>Check the ranked network list</strong></p>
        <ol start={2}>
          <li>Select the <strong>Network</strong> tab in the left-hand menu panel.<br/>{"->"} The Network page is
            opened. All networks in the vehicle are shown in the ranked list in the middle panel. The <img
              className="icon"
              src="https://cdn.volvotechinfo.com/vida-basic-training/Network%20Fault%20Tracing%20Overview/Network%20fault%20tracing%20overview%20VDS_image2.jpg"
              alt="Network fault tracing overview VDS_image2"/> icon indicates a network that has components with set
            DTCs. Networks without this symbol does not have any DTCs. The list is ranked, meaning that the network that
            is most likely to have faults is presented at the top of the list.
          </li>
          <li>Select the network with a <img className="icon"
                                             src="https://cdn.volvotechinfo.com/vida-basic-training/Network%20Fault%20Tracing%20Overview/Network%20fault%20tracing%20overview%20VDS_image2.jpg"
                                             alt="Network fault tracing overview VDS_image2"/> icon and click the <img
            className="icon"
            src="https://cdn.volvotechinfo.com/vida-basic-training/Network%20Fault%20Tracing%20Overview/Network%20fault%20tracing%20overview%20VDS_image3.jpg"
            alt="Network fault tracing overview VDS_image3"/> to see the components it consists of.<br/>{"->"} The
            selected network in the list is expanded and you can see all components it consists of.
          </li>
          <li>In this case the first action in the list to check is a wire. Since the wire is presented first in the
            list it is the most probable cause of the problem. The best way to find out more about the wire is by using
            the wiring diagram. Select the wire in the selected network{"'"}s ranked list.
          </li>
        </ol>
        <p><strong>Start fault tracing</strong></p>
        <ol start={5}>
          <li>To find out where the wire is located in the wiring diagram, click the <strong>Wiring
            Diagrams</strong> tab.<br/>{"->"} The wiring diagram is shown with the signal marked in bold. A dashed line
            in the wiring diagram indicates that the wire is most likely the cause of the problem.
          </li>
          <li>Right click the dashed line and select <strong>Signal information</strong>.<br/>{"->"} A pop-up is opened.
          </li>
          <li>Identify what the wire is connected to and how to measure the signal and then close the pop-up.</li>
          <li>Right click the wire (the dashed line) once again and select <strong>Cable harness
            image</strong>.<br/>{"->"} A pop-up is opened.
          </li>
          <li>Identify where in the vehicle the wire is located. The connector is marked in green. Measure the signal
            and do a visual inspection of the pin and the wire in the vehicle to identify the faults. Remedy if needed.
          </li>
          <li>If no fault was found, continue with the next component in the ranked list.</li>
        </ol>
      </VidaSection>

      {/* Network fault tracing (D2 and GGD) */}
      <Spacer/>
      <VidaSection title="Network fault tracing (D2 and GGD)" id="network-fault-tracing-d2-and-ggd">
        <p><strong>Fault tracing a vehicle{"'"}s network</strong></p>
        <p>This procedure describes how to fault trace a D2 or GGD protocol-based vehicle{"'"}s network.</p>
        <Note body={<p>This procedure is only applicable for D2 and GGD protocol-based vehicles.</p>}/>
        <ol>
          <li>Start by connecting the vehicle to work
            with.<br/>{"->"} The <strong>Planning &amp; Diagnostics</strong> tab is opened and assigned its own tab by
            default as soon as the vehicle is connected.
          </li>
          <li>Select <strong>Network</strong> from the left-hand menu panel.<br/>{"->"} The network fault tracing
            functionalities are shown. The vehicle graphic displays all control modules in the vehicle.
          </li>
          <li>Control modules marked in red in the vehicle graphic are not responding (faulty). Gray control modules are
            not part of the vehicle{"'"}s configuration or do not respond because of other faulty control units. An
            explanation of all possible colors of control units in the vehicle graphic is accessed by clicking the <img
              className="icon"
              src="https://cdn.volvotechinfo.com/vida-basic-training/Network%20Fault%20Tracing%20Overview/Network%20fault%20tracing%20(D2%20and%20GGD)_image.jpg"
              alt="Network fault tracing (D2 and GGD)_image"/> icon.
          </li>
          <li>To continue the fault tracing, select a faulty control unit in the vehicle graphic.<br/>{"->"} Reference
            information is listed in the panel below the vehicle graphic.
          </li>
          <li>Select reference information and read it to find a solution. Selected reference information is shown under
            the tabs in the right-hand panel. The following main categories of reference information are available:<br/>
            <ul>
              <li>General Diagnostics and Tests</li>
              <li>Component location</li>
              <li>Specifications, electrical/electronic</li>
              <li>Specifications, other</li>
            </ul>
          </li>
        </ol>
      </VidaSection>
    </VidaSection>
    <style jsx>{`
      .icon {
        width: 25px;
      }
    `}</style>
  </div>
}

export default NetworkFaultTracingOverview;
